import CentralLabOrderList from "issara-sdk/apis/CentralLabOrderList_apps_LAB";
import CheckPasswordAPI2 from "issara-sdk/apis/CheckPasswordAPI2_users";
import DoctorFeeOrderDetail from "issara-sdk/apis/DoctorFeeOrderDetail_apps_DFC";
import DoctorFeeOrderList from "issara-sdk/apis/DoctorFeeOrderList_apps_DFC";
import DoctorOrderCancel from "issara-sdk/apis/DoctorOrderCancel_apps_DPO";
import DoctorOrderDetail from "issara-sdk/apis/DoctorOrderDetail_apps_DPO";
import DoctorOrderList from "issara-sdk/apis/DoctorOrderList_apps_DPO";
import DrugOrderActionLogList from "issara-sdk/apis/DrugOrderActionLogList_apps_TPD";
import EncounterPatientListOptimized from "issara-sdk/apis/EncounterPatientListOptimized_core";
import ImagingOrderDetail from "issara-sdk/apis/ImagingOrderDetail_apps_IME";
import ImagingOrderItemActionLogList from "issara-sdk/apis/ImagingOrderItemActionLogList_apps_IME";
import MiscellaneousOrderDetail from "issara-sdk/apis/MiscellaneousOrderDetail_coreM";
import MiscellaneousOrderList from "issara-sdk/apis/MiscellaneousOrderList_core";
import SupplyOrderActionLogList from "issara-sdk/apis/SupplyOrderActionLogList_apps_MSD";

// serializer
import CentralLabOrderListSerializer from "issara-sdk/types/CentralLabOrderListSerializer_apps_LAB";
import DoctorFeeOrderListPreviewSerializer from "issara-sdk/types/DoctorFeeOrderListPreviewSerializer_apps_DFC";
import DoctorOrderListSerializer from "issara-sdk/types/DoctorOrderListSerializer_apps_DPO";
import DrugOrderActionLogSerializer from "issara-sdk/types/DrugOrderActionLogSerializer_apps_TPD";
import EncounterPatientSerializer from "issara-sdk/types/EncounterPatientSerializer_core";
import ImagingOrderItemActionLogSerializer from "issara-sdk/types/ImagingOrderItemActionLogSerializer_apps_IME";
import ImagingOrderSerializer from "issara-sdk/types/ImagingOrderSerializer_apps_IME";
import MiscellaneousOrderSerializer from "issara-sdk/types/MiscellaneousOrderSerializer_core";
import SupplyOrderActionLogSerializer from "issara-sdk/types/SupplyOrderActionLogSerializer_apps_MSD";
import moment from "moment";

import { parseDate } from "react-lib/utils/dateUtils";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  CANCEL: "CANCEL",
  CANCEL_FEE: "CANCEL_FEE",
  LOAD_LOG: "LOAD_LOG",
  REFRESH: "REFRESH",
} as const;

export const SEQUENCE_NAME = "PatientOrdersV4" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  PatientOrdersV4Sequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    encounter: EncounterType | null;
    orderList: DoctorOrderListSerializer[];
    labOrderList: CentralLabOrderListSerializer[];
    doctorFeeList: DoctorFeeOrderListPreviewSerializer[];
    miscList: MiscellaneousOrderSerializer[];
    lastConfirmed: LastConfirmed | null;
    // Log ของคำสั่งที่เปิดดู
    orderLog: OrderLog | null;
  }> | null;
}>;

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage">;

/** ประเภทคำสั่งที่มี API Log */
export type LogKind = "drug" | "supply" | "imaging";

/** Log ของคำสั่ง 1 รายการ เรียงตามเวลา */
export type OrderLog = {
  orderId: number;
  kind: LogKind;
  items: (
    | DrugOrderActionLogSerializer
    | SupplyOrderActionLogSerializer
    | ImagingOrderItemActionLogSerializer
  )[];
};

/** เวลายืนยันคำสั่งล่าสุดของ Encounter (DD/MM/YYYY [HH:mm] พ.ศ.) */
export type LastConfirmed = { time: string; by?: number };

/** แถวคิวที่เปิด Encounter (/apis/core/encounter-patient-optimized/) */
export type EncounterType = Pick<
  EncounterPatientSerializer,
  "id" | "hn" | "created" | "doctor_orders"
>;

export type Event =
  | { message: "RunSequence"; params: Record<string, unknown> }
  /** #86608-2: สั่งเปลี่ยนบนหน้าคำสั่ง -> MainHIS อ่านคิวใหม่ทั้งชุด (ดู case ใน MainHISController.tsx) */
  | { message: "HandleRefreshEncounter"; params: Record<string, unknown> };

export type Data = { division?: number; device?: number };

export type SeqState = BaseSeqState<SequenceName> & { encounter?: EncounterType };

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.CANCEL]: {
    btnAction?: string;
    id: number;
    note: string;
    password: string;
    onSuccess?: () => void;
  };
  [ACTIONS.CANCEL_FEE]: {
    btnAction?: string;
    kind: "df" | "misc";
    id: number;
    note: string;
    password: string;
    onSuccess?: () => void;
  };
  [ACTIONS.LOAD_LOG]: { btnAction?: string; orderId: number; kind: LogKind };
  [ACTIONS.REFRESH]: {};
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  PatientOrdersV4Sequence: null,
};

export const DataInitial: Data = {};

export const ORDER_STATUS = {
  CANCEL: "CANCEL",
  PENDING: "PENDING",
  PERFORMED: "PERFORMED",
} as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  controller.setState(
    {
      PatientOrdersV4Sequence: {
        sequenceIndex: "Action",
        encounter: params.encounter ?? null,
        orderList: [],
        labOrderList: [],
        doctorFeeList: [],
        miscList: [],
        lastConfirmed: null,
        orderLog: null,
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.REFRESH });
    },
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.CANCEL]: HandleCancel,
    [ACTIONS.CANCEL_FEE]: HandleCancelFee,
    [ACTIONS.LOAD_LOG]: HandleLoadLog,
    [ACTIONS.REFRESH]: HandleRefresh,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    return handler(controller, params);
  }

  console.error(`No handler found for action: ${action}`);

  return () => {
    // ---
  };
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleRefresh: Handler<Params<Actions["REFRESH"]>> = async (controller, params) => {
  let state = controller.getState();

  const encounter = state.PatientOrdersV4Sequence?.encounter;

  if (!encounter?.id) {
    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.REFRESH]: BLC_STATUS.LOADING },
  });

  const extra = { division: controller.data.division, device: controller.data.device };

  // id ของคำสั่งอยู่ใน doctor_orders ของแถวคิว ค้นด้วย HN + วันที่สร้าง Encounter (พ.ศ.)
  const date = encounter.created?.split(" ")[0];

  const [[queue, queueError], labOrder, doctorFee, miscellaneous] = await Promise.all([
    EncounterPatientListOptimized.get({
      apiToken: controller.apiToken,
      params: { patient__hn: encounter.hn, from_date: date, to_date: date },
      extra,
    }),
    CentralLabOrderList.list({
      apiToken: controller.apiToken,
      params: { encounter: encounter.id },
      extra,
    }),
    DoctorFeeOrderList.list({
      apiToken: controller.apiToken,
      params: { encounter: encounter.id, limit: 99999 },
      extra,
    }),
    MiscellaneousOrderList.list({
      apiToken: controller.apiToken,
      params: { encounter_id: encounter.id, active: true, limit: 99999 },
      extra,
    }),
  ]);

  const queueItems: EncounterPatientSerializer[] = queue?.items ?? [];
  const labOrderList: CentralLabOrderListSerializer[] = labOrder[0]?.items ?? [];
  const queueItem = queueItems.find((item) => item.id === encounter.id);
  const doctorOrders = queueItem?.doctor_orders ?? encounter.doctor_orders ?? [];

  // คำสั่งทั้งหมดของ medical record รวมที่ยกเลิกแล้ว
  const [emrOrders, emrError] = queueItem?.medical_record
    ? await DoctorOrderList.list({
        params: { emr: queueItem.medical_record, limit: 999 },
        apiToken: controller.apiToken,
        extra,
      })
    : [null, null];

  const listedOrders: DoctorOrderListSerializer[] = emrOrders?.items ?? [];
  const listedIds = new Set(listedOrders.map((item) => item.id));

  // id ที่ไม่ได้มากับรายการข้างบน: จากคิว และรายการแลป
  const orderIds = [
    ...new Set([...doctorOrders.map((item) => item.id), ...labOrderList.map((item) => item.id)]),
  ].filter((id) => !listedIds.has(id));

  const orderDetails = await Promise.all(
    orderIds.map((id) =>
      DoctorOrderDetail.retrieve({ pk: id, apiToken: controller.apiToken, extra }),
    ),
  );

  const orderList: DoctorOrderListSerializer[] = [
    ...listedOrders,
    ...orderDetails.map(([order]) => order).filter(Boolean),
  ];

  const error =
    queueError ??
    emrError ??
    orderDetails.find(([, err]) => err)?.[1] ??
    labOrder[1] ??
    doctorFee[1] ??
    miscellaneous[1];

  state = controller.getState();

  const lastConfirmed = latestConfirmed([
    ...doctorOrders.map((item) => ({ time: item.order_time, by: item.order_by })),
    ...labOrderList.map((item) => ({ time: item.order_time, by: item.order_by })),
  ]);

  controller.setState({
    PatientOrdersV4Sequence: {
      ...state.PatientOrdersV4Sequence,
      encounter: { ...encounter, doctor_orders: doctorOrders },
      lastConfirmed,
      orderList,
      labOrderList,
      doctorFeeList: doctorFee[0]?.items ?? [],
      miscList: miscellaneous[0]?.items ?? [],
    },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.REFRESH]: BLC_STATUS.SUCCESS },
  });
};

const HandleCancel: Handler<Params<Actions["CANCEL"]>> = async (controller, params) => {
  const state = controller.getState();

  const btnAction = params.btnAction ?? BTN_ACTS.CANCEL;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnAction]: BLC_STATUS.LOADING },
  });

  const passwordError = await checkPassword(controller, params.password);

  if (passwordError) {
    SetErrorMessage(controller, { ...params, btnAction, error: passwordError });

    return;
  }

  const [, error] = await DoctorOrderCancel.put({
    pk: params.id,
    data: { action: "CANCEL", note: params.note.trim() },
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction, error });

    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnAction]: BLC_STATUS.SUCCESS },
  });

  /* #86608-2: ยกเลิกคำสั่งแลป -> คอลัมน์คำสั่ง/สถานะในตารางคิวต้องเปลี่ยนทันที */
  const encounterId = state.PatientOrdersV4Sequence?.encounter?.id;
  if (encounterId) {
    controller.handleEvent({ message: "HandleRefreshEncounter", params: { encounterId } });
  }

  params.onSuccess?.();

  await HandleRefresh(controller, { sequence: SEQUENCE_NAME, action: ACTIONS.REFRESH });
};

/** ลบ Doctor Fee / รายการเบ็ดเตล็ดที่บันทึกแล้ว */
const HandleCancelFee: Handler<Params<Actions["CANCEL_FEE"]>> = async (controller, params) => {
  const state = controller.getState();

  const btnAction = params.btnAction ?? BTN_ACTS.CANCEL_FEE;
  const note = params.note.trim();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnAction]: BLC_STATUS.LOADING },
  });

  const passwordError = await checkPassword(controller, params.password);

  if (passwordError) {
    SetErrorMessage(controller, { ...params, btnAction, error: passwordError });

    return;
  }

  const extra = { division: controller.data.division, device: controller.data.device };

  const [, error] =
    params.kind === "df"
      ? await DoctorFeeOrderDetail.delete({
          pk: params.id,
          apiToken: controller.apiToken,
          extra: { ...extra, data: { remark: note } },
        })
      : await MiscellaneousOrderDetail.delete({
          pk: params.id,
          data: { note },
          apiToken: controller.apiToken,
          extra,
        });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction, error });

    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnAction]: BLC_STATUS.SUCCESS },
  });

  /* #86608-2: ลบ fee/เบ็ดเตล็ด -> แถวคิว (คำสั่ง/สถานะ) ต้องอัปเดตตาม */
  const canceledEncounterId = state.PatientOrdersV4Sequence?.encounter?.id;
  if (canceledEncounterId) {
    controller.handleEvent({
      message: "HandleRefreshEncounter",
      params: { encounterId: canceledEncounterId },
    });
  }

  params.onSuccess?.();

  await HandleRefresh(controller, { sequence: SEQUENCE_NAME, action: ACTIONS.REFRESH });
};

const HandleLoadLog: Handler<Params<Actions["LOAD_LOG"]>> = async (controller, params) => {
  let state = controller.getState();

  const btnAction = params.btnAction ?? BTN_ACTS.LOAD_LOG;

  controller.setState({
    PatientOrdersV4Sequence: { ...state.PatientOrdersV4Sequence, orderLog: null },
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnAction]: BLC_STATUS.LOADING },
  });

  let items: OrderLog["items"] = [];
  let error: unknown = null;

  if (params.kind === "drug") {
    const [res, err] = await DrugOrderActionLogList.get({
      pk: params.orderId,
      apiToken: controller.apiToken,
      extra: { division: controller.data.division, device: controller.data.device },
    });

    items = res?.items ?? [];
    error = err;
  } else if (params.kind === "supply") {
    const [res, err] = await SupplyOrderActionLogList.list({
      pk: params.orderId,
      params: { limit: 999 },
      apiToken: controller.apiToken,
      extra: { division: controller.data.division, device: controller.data.device },
    });

    items = res?.items ?? [];
    error = err;
  } else {
    const [order, orderError] = await ImagingOrderDetail.retrieve({
      pk: params.orderId,
      apiToken: controller.apiToken,
      extra: { division: controller.data.division, device: controller.data.device },
    });

    const orderItems: ImagingOrderSerializer["order_items"] = order?.order_items ?? [];

    const logs = await Promise.all(
      orderItems.map((orderItem) =>
        ImagingOrderItemActionLogList.list({
          order_item_id: orderItem.id,
          params: { limit: 999 },
          apiToken: controller.apiToken,
          extra: { division: controller.data.division, device: controller.data.device },
        }),
      ),
    );

    items = logs.flatMap(([res]) => res?.items ?? []);
    error = orderError ?? logs.find(([, err]) => err)?.[1] ?? null;
  }

  state = controller.getState();

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction, error });

    return;
  }

  controller.setState({
    PatientOrdersV4Sequence: {
      ...state.PatientOrdersV4Sequence,
      orderLog: {
        orderId: params.orderId,
        kind: params.kind,
        items: [...items].sort((a, b) => (a.datetime ?? "").localeCompare(b.datetime ?? "")),
      },
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnAction]: BLC_STATUS.SUCCESS },
  });
};

/** ====================================================  */
/**                        Utils                          */
/** ====================================================  */
/** ตรวจรหัสผ่านของผู้ใช้ก่อนยกเลิก คืน error หรือ null */
const checkPassword = async (controller: Parameters<Handler>[0], password: string) => {
  if (!password) {
    return "กรุณาใส่ Password";
  }

  const [result, error, response] = await CheckPasswordAPI2.post({
    data: { password },
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  });

  // 404 = รหัสผ่านไม่ถูกต้อง
  if (response?.response?.status === 404 || (!error && !result?.result)) {
    return "Password ไม่ถูกต้อง · ยังไม่ได้ยกเลิก Order";
  }

  return error ?? null;
};

/** ISO หรือ "DD/MM/YYYY [HH:mm]" (พ.ศ.) */
const timeValue = (value: string) => {
  const iso = moment(value, moment.ISO_8601, true);

  if (iso.isValid()) {
    return iso.valueOf();
  }

  const [date, time = ""] = value.split(" ");

  return (
    (parseDate(date, true)?.valueOf() ?? 0) +
    moment.duration(time.replace(/[[\]]/g, "")).asMilliseconds()
  );
};

const latestConfirmed = (list: Partial<LastConfirmed>[]): LastConfirmed | null => {
  let latest: LastConfirmed | null = null;

  for (const item of list) {
    if (item.time && (!latest || timeValue(item.time) > timeValue(latest.time))) {
      latest = { time: item.time, by: item.by };
    }
  }

  return latest;
};
